// One shared period vocabulary for the analytics pages.
//
// /analytics, /analytics/expenses and /analytics/:metricType all filter on the
// same period value, and that value travels between them in the ?period= query
// string, so the option list, the label and the query handling have to come
// from a single place. Before this module each page built its own list and the
// selection silently reset on navigation.

export const ALL_TIME = "all";

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// How far back the selector reaches, counted from the current year.
const YEARS_BACK = 3;
// Month granularity is only offered for the current and the previous year.
const MONTH_YEARS_BACK = 1;

export function getQuarterOptions(now = new Date()) {
  const currentYear = now.getFullYear();
  const options = [];
  for (let year = currentYear; year >= currentYear - YEARS_BACK; year--) {
    for (let q = 4; q >= 1; q--) {
      options.push({ value: `Q${q}-${year}`, label: `Q${q} ${year}` });
    }
  }
  return options;
}

export function getYearOptions(now = new Date()) {
  const currentYear = now.getFullYear();
  const options = [];
  for (let year = currentYear; year >= currentYear - YEARS_BACK; year--) {
    options.push({ value: `year-${year}`, label: `${year}` });
  }
  return options;
}

// Current year up to the current month, then every month of the year before it.
export function getMonthOptions(now = new Date()) {
  const currentYear = now.getFullYear();
  const options = [];
  for (let m = now.getMonth(); m >= 0; m--) {
    options.push({
      value: `month-${m + 1}-${currentYear}`,
      label: `${MONTH_NAMES[m]} ${currentYear}`,
    });
  }
  for (let year = currentYear - 1; year >= currentYear - MONTH_YEARS_BACK; year--) {
    for (let m = 11; m >= 0; m--) {
      options.push({
        value: `month-${m + 1}-${year}`,
        label: `${MONTH_NAMES[m]} ${year}`,
      });
    }
  }
  return options;
}

// The flat list, for pages that show the periods in a single dropdown.
export function getPeriodOptions(now = new Date()) {
  return [
    { value: ALL_TIME, label: "All Time" },
    ...getYearOptions(now),
    ...getMonthOptions(now),
    ...getQuarterOptions(now),
  ];
}

// "Q3-2026" -> "Q3 2026", "year-2026" -> "2026", "month-9-2026" -> "Sep 2026".
export function formatPeriodLabel(value) {
  if (!value || value === ALL_TIME) return "All Time";
  if (value.startsWith("year-")) return value.replace("year-", "");
  if (value.startsWith("month-")) {
    const parts = value.split("-");
    const name = MONTH_NAMES[parseInt(parts[1], 10) - 1];
    return name ? `${name.substring(0, 3)} ${parts[2]}` : value;
  }
  return value.replace("-", " ");
}

export function isPeriodValue(value, now = new Date()) {
  if (!value) return false;
  if (value === ALL_TIME) return true;
  return getPeriodOptions(now).some((option) => option.value === value);
}

// Read the period out of the URL, falling back to "all" for anything we do not
// recognise, so a hand-edited query string cannot break the page.
export function readPeriodParam(searchParams, now = new Date()) {
  const value = searchParams?.get("period");
  return isPeriodValue(value, now) ? value : ALL_TIME;
}

// The next query string after a period change, keeping any other parameters.
export function setPeriodParam(searchParams, value) {
  const next = new URLSearchParams(searchParams || undefined);
  if (!value || value === ALL_TIME) {
    next.delete("period");
  } else {
    next.set("period", value);
  }
  return next;
}

// An in-app path that carries the selected period, so navigating between the
// analytics pages keeps the filter the user was looking at.
export function withPeriod(path, value) {
  if (!value || value === ALL_TIME) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}period=${encodeURIComponent(value)}`;
}
